import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtUSD, formatApiError } from "../lib/api";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { ShieldCheck, Download, Heart, Plus, Minus, FlaskConical, Snowflake, Beaker, ListChecks, ArrowRight, X, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";
import BacWaterUpsellModal, { BAC_SLUG } from "../components/BacWaterUpsellModal";

const TIERS = [
    { qty: 1, label: "1 VIAL", discount: 0, badge: "" },
    { qty: 2, label: "2 VIALS", discount: 3.6, badge: "MOST POPULAR" },
    { qty: 3, label: "3 VIALS", discount: 5.3, badge: "BEST VALUE" },
];

export default function ProductPage() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [qty, setQty] = useState(1);
    const [tab, setTab] = useState("overview");
    const [coa, setCoa] = useState(null);
    const { addItem } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [wished, setWished] = useState(false);
    const [activeMedia, setActiveMedia] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [justAdded, setJustAdded] = useState(false);
    const [showBacUpsell, setShowBacUpsell] = useState(false);

    useEffect(() => {
        setProduct(null);
        setActiveMedia(0);
        api.get(`/products/${slug}`).then((r) => {
            setProduct(r.data);
            return api.get(`/coas?q=${r.data.slug}`);
        }).then((r) => {
            if (r?.data?.length) setCoa(r.data[0]);
        });
        api.get(`/products/${slug}/related`).then((r) => setRelated(r.data));
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [slug]);

    useEffect(() => {
        if (!user) return;
        api.get("/wishlist").then((r) => {
            setWished(r.data.some((p) => p.slug === slug));
        }).catch(() => {});
    }, [user, slug]);

    if (!product) return <div className="pt-52 text-center text-secondary">Loading research data…</div>;

    const tier = TIERS.reduce((acc, t) => (qty >= t.qty ? t : acc), TIERS[0]);
    const gross = product.price * qty;
    const net = gross * (1 - tier.discount / 100);
    const savings = gross - net;

    const onAdd = () => {
        addItem(product, qty);
        toast.success(`${qty}× ${product.name} added to your research order`, {
            duration: 6000,
            action: {
                label: "View Cart →",
                onClick: () => navigate("/cart"),
            },
        });
        setJustAdded(true);
        setTimeout(() => setJustAdded(false), 4000);
        // Offer bac water on any peptide that needs reconstitution (skip if it's bac water itself).
        if (product.slug !== BAC_SLUG) {
            setTimeout(() => setShowBacUpsell(true), 350);
        }
    };

    const onWishlist = async () => {
        if (!user) { toast.error("Sign in to save to wishlist"); return; }
        try {
            if (wished) {
                await api.delete(`/wishlist/${product.id}`);
                setWished(false);
            } else {
                await api.post("/wishlist", { product_id: product.id });
                setWished(true);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid="product-page" className="pt-48 pb-24 bg-[var(--bg)]">
            {/* Research-use band */}
            <div className="bg-[var(--bg-carbon)] border-y border-[var(--border)] py-2.5">
                <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-3">
                    <span className="w-1.5 h-1.5 bg-[var(--warning)] pulse-gold" />
                    <p className="data-label text-[var(--warning)]">FOR LABORATORY RESEARCH USE ONLY · NOT FOR HUMAN CONSUMPTION</p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Image (sticky) */}
                    <div className="lg:col-span-6 lg:sticky lg:top-32 lg:self-start">
                        {(() => {
                            const gallery = product.gallery || [];
                            // Tolerate either plain URL strings or {url} objects in gallery entries.
                            const normalize = (g) => typeof g === "string" ? g : (g && g.url) || "";
                            const media = [product.image, ...gallery.map(normalize).filter(Boolean)];
                            const current = media[activeMedia] || product.image;
                            const isVialRender = activeMedia === 0;
                            return (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setLightboxOpen(true)}
                                        className="card tile-cream aspect-square overflow-hidden flex items-center justify-center p-10 lg:p-16 w-full cursor-zoom-in"
                                        data-testid="product-media-main"
                                        aria-label="Open larger view"
                                    >
                                        <img
                                            src={current}
                                            alt={product.name}
                                            className={isVialRender ? "max-h-[70%] max-w-[55%] object-contain" : "max-h-full max-w-full object-contain"}
                                            style={isVialRender ? { filter: "drop-shadow(0 30px 50px rgba(15, 23, 42, 0.22))" } : undefined}
                                        />
                                    </button>
                                    {media.length > 1 && (
                                        <div className="mt-4 flex gap-3 overflow-x-auto pb-1" data-testid="product-thumbnails">
                                            {media.map((src, i) => (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    onClick={() => setActiveMedia(i)}
                                                    className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition flex items-center justify-center bg-white ${activeMedia === i ? "border-[var(--brand)]" : "border-[var(--border)] hover:border-[var(--brand-soft)]"}`}
                                                    data-testid={`thumb-${i}`}
                                                    aria-label={i === 0 ? "Product image" : `Document ${i}`}
                                                >
                                                    <img
                                                        src={src}
                                                        alt=""
                                                        className={i === 0 ? "max-h-[70%] max-w-[60%] object-contain" : "w-full h-full object-cover"}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                        <div className="mt-4 grid grid-cols-4 gap-3">
                            <div className="card p-3 text-center">
                                <div className="data-label">Strength</div>
                                <div className="font-mono text-sm text-primary mt-1">{product.strength}</div>
                            </div>
                            <div className="card p-3 text-center">
                                <div className="data-label">Vial</div>
                                <div className="font-mono text-sm text-primary mt-1">{product.vial_size}</div>
                            </div>
                            <div className="card p-3 text-center">
                                <div className="data-label">Purity</div>
                                <div className="font-mono text-sm text-brand mt-1">{product.purity}</div>
                            </div>
                            <div className="card p-3 text-center">
                                <div className="data-label">Batch</div>
                                <div className="font-mono text-[0.65rem] text-primary mt-1 truncate">{product.batch_number}</div>
                            </div>
                        </div>
                    </div>

                    {/* Details */}
                    <div className="lg:col-span-6">
                        <div className="data-label text-brand mb-2">{product.category}</div>
                        <h1 className="font-serif text-5xl md:text-6xl leading-none mb-4">{product.name}</h1>
                        {product.aliases && product.aliases.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-6">
                                {product.aliases.map((a) => <span key={a} className="pill">{a}</span>)}
                            </div>
                        )}
                        <VariantSelector product={product} />
                        <p className="text-secondary leading-relaxed mb-8">{product.short_description}</p>

                        <div className="hairline mb-8" />

                        {/* Pricing + Quantity tiers */}
                        <div className="mb-8">
                            <div className="flex items-baseline justify-between mb-6">
                                <div>
                                    <div className="data-label">Unit Price</div>
                                    <div className="font-serif text-4xl text-brand mt-1">{fmtUSD(product.price)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="data-label">Your Total</div>
                                    <div className="font-serif text-4xl mt-1">{fmtUSD(net)}</div>
                                    {savings > 0 && <div className="data-label text-brand mt-1">Save {fmtUSD(savings)}</div>}
                                </div>
                            </div>

                            <div className="data-label mb-3">Bundle & Save</div>
                            <div className="grid grid-cols-3 gap-3 mb-6">
                                {TIERS.map((t) => (
                                    <button
                                        key={t.qty}
                                        data-testid={`tier-${t.qty}`}
                                        onClick={() => setQty(t.qty)}
                                        className={`p-4 border transition-all ${qty === t.qty ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--border)] hover:border-[var(--border-light)]"}`}
                                    >
                                        {t.badge && <div className={`data-label mb-2 ${qty === t.qty ? "text-brand" : "text-secondary"}`}>{t.badge}</div>}
                                        <div className="font-mono text-sm text-primary">{t.label}</div>
                                        <div className={`font-mono text-xs mt-1 ${t.discount ? "text-brand" : "text-muted"}`}>
                                            {t.discount ? `${t.discount}% OFF` : "Standard"}
                                        </div>
                                    </button>
                                ))}
                            </div>

                            <div className="flex items-center gap-4 mb-6">
                                <div className="data-label">Quantity</div>
                                <div className="flex items-center border border-[var(--border)]">
                                    <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-dec" className="btn btn-ghost px-3 py-2"><Minus size={12} /></button>
                                    <span data-testid="qty-display" className="font-mono text-sm px-5 py-2 border-x border-[var(--border)]">{qty}</span>
                                    <button onClick={() => setQty(qty + 1)} data-testid="qty-inc" className="btn btn-ghost px-3 py-2"><Plus size={12} /></button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                                <button onClick={onAdd} data-testid="add-to-cart-btn" className="btn btn-solid">
                                    Add to Research Order · {fmtUSD(net)} <ArrowRight size={14} />
                                </button>
                                <button onClick={onWishlist} data-testid="wishlist-btn" className="btn">
                                    <Heart size={14} className={wished ? "fill-[var(--brand)] text-[var(--brand)]" : ""} />
                                </button>
                            </div>

                            {justAdded && (
                                <div
                                    data-testid="cart-added-banner"
                                    className="mt-3 p-3 border border-[var(--brand)] bg-[var(--brand-soft)] flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-1 duration-300"
                                >
                                    <div className="flex items-center gap-2 font-mono text-xs text-primary">
                                        <ShoppingCart size={14} className="text-brand" />
                                        Added to your cart. Ready to check out?
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={() => navigate("/cart")} data-testid="goto-cart-btn" className="btn btn-gold">
                                            View Cart <ArrowRight size={12} />
                                        </button>
                                        <button onClick={() => navigate("/checkout")} data-testid="goto-checkout-btn" className="btn">
                                            Checkout
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-3 mt-4">
                                <ShieldCheck size={14} className="text-brand" />
                                <span className="data-label">In Stock · {product.stock} units · Ships within 24h after payment confirmation</span>
                            </div>
                        </div>

                        <div className="hairline mb-8" />

                        {/* Tabs */}
                        <div className="flex border-b border-[var(--border)] mb-6 overflow-x-auto">
                            {[
                                { id: "overview", label: "Overview" },
                                { id: "research", label: "Research Info" },
                                { id: "storage", label: "Storage & Recon" },
                                { id: "specs", label: "Specifications" },
                                { id: "coa", label: "COA / Testing" },
                            ].map((t) => (
                                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`} className={`tab-trigger ${tab === t.id ? "active" : ""}`}>
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        <div className="min-h-[200px]">
                            {tab === "overview" && (
                                <div className="space-y-4">
                                    <p className="text-secondary leading-relaxed">{product.description}</p>
                                </div>
                            )}
                            {tab === "research" && (
                                <div className="space-y-5">
                                    <ResearchProse text={product.research_info} />
                                    {product.research_studies && product.research_studies.length > 0 && (
                                        <div>
                                            <div className="data-label text-brand mb-3">REFERENCED STUDIES</div>
                                            <ul className="space-y-2">
                                                {product.research_studies.map((s, i) => (
                                                    <li key={i} className="flex gap-3 text-sm text-secondary">
                                                        <span className="font-mono text-brand">[{i + 1}]</span>
                                                        <span>{s}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                            {tab === "storage" && (
                                <div className="space-y-6">
                                    <div className="flex gap-4">
                                        <Snowflake className="text-brand shrink-0" size={20} />
                                        <div>
                                            <div className="data-label text-brand mb-2">STORAGE</div>
                                            <p className="text-secondary text-sm leading-relaxed">{product.storage}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <Beaker className="text-brand shrink-0" size={20} />
                                        <div>
                                            <div className="data-label text-brand mb-2">RECONSTITUTION</div>
                                            <p className="text-secondary text-sm leading-relaxed">{product.reconstitution}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {tab === "specs" && (
                                <div className="card divide-y divide-[var(--border)]">
                                    {Object.entries(product.specifications || {}).map(([k, v]) => (
                                        <div key={k} className="flex justify-between py-3 px-4 font-mono text-sm">
                                            <span className="text-muted uppercase tracking-wider text-xs">{k}</span>
                                            <span className="text-primary text-right">{v}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {tab === "coa" && (
                                <div className="space-y-4">
                                    {coa ? (
                                        <div className="card p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div>
                                                    <div className="data-label text-brand mb-2">CERTIFICATE OF ANALYSIS</div>
                                                    <div className="font-mono text-sm text-primary">{coa.batch_number}</div>
                                                </div>
                                                {coa.pdf_url ? (
                                                    <a href={coa.pdf_url} target="_blank" rel="noopener noreferrer" className="btn btn-solid" data-testid="product-coa-download">
                                                        <Download size={14} /> Download COA PDF
                                                    </a>
                                                ) : (
                                                    <Link to={`/coa`} className="btn">
                                                        <Download size={14} /> All COAs
                                                    </Link>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-2 gap-4 font-mono text-xs">
                                                <div><span className="text-muted">PURITY</span><div className="text-brand text-base mt-1">{coa.purity}</div></div>
                                                <div><span className="text-muted">METHOD</span><div className="text-primary text-sm mt-1">{coa.test_method}</div></div>
                                                <div><span className="text-muted">LAB</span><div className="text-primary text-sm mt-1">{coa.lab_name}</div></div>
                                                <div><span className="text-muted">DATE</span><div className="text-primary text-sm mt-1">{coa.test_date}</div></div>
                                            </div>
                                            {coa.notes && <p className="text-xs text-secondary mt-4 leading-relaxed border-t border-[var(--border)] pt-4">{coa.notes}</p>}
                                        </div>
                                    ) : (
                                        <div className="card p-6"><p className="text-secondary">COA for this lot is being uploaded. Contact us to receive it directly.</p></div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Related */}
                {related.length > 0 && (
                    <section className="mt-24">
                        <div className="data-label text-brand mb-3">RELATED COMPOUNDS</div>
                        <h2 className="font-serif text-3xl md:text-4xl mb-8">Continue exploring</h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                        </div>
                    </section>
                )}
            </div>

            {/* Lightbox */}
            {lightboxOpen && (() => {
                const gallery = product.gallery || [];
                const normalize = (g) => typeof g === "string" ? g : (g && g.url) || "";
                const media = [product.image, ...gallery.map(normalize).filter(Boolean)];
                const current = media[activeMedia] || product.image;
                return (
                    <div
                        className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
                        onClick={() => setLightboxOpen(false)}
                        data-testid="lightbox"
                    >
                        <button
                            type="button"
                            onClick={() => setLightboxOpen(false)}
                            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
                            aria-label="Close"
                            data-testid="lightbox-close"
                        >
                            <X size={20} />
                        </button>
                        <img
                            src={current}
                            alt={product.name}
                            className="max-w-[92vw] max-h-[88vh] object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                );
            })()}
            <BacWaterUpsellModal open={showBacUpsell} onClose={() => setShowBacUpsell(false)} />
        </div>
    );
}


/* Renders long-form research copy stored as plain text in product.research_info.
 * Recognizes:
 *  - "## Heading" lines → section heading (gold caps)
 *  - "**bold**" inline emphasis
 *  - "- item" → bullet
 *  - blank line → paragraph break
 */
function ResearchProse({ text }) {
    if (!text) return null;
    const blocks = String(text).split(/\n\s*\n/);
    return (
        <div className="space-y-4 text-secondary leading-relaxed">
            {blocks.map((block, i) => {
                const lines = block.split(/\n/);
                // Heading block
                if (lines[0].startsWith("## ")) {
                    return (
                        <div key={i} className="pt-2">
                            <div className="data-label text-brand mb-2">{lines[0].replace(/^##\s+/, "")}</div>
                            {lines.slice(1).length > 0 && (
                                <ProseLines lines={lines.slice(1)} />
                            )}
                        </div>
                    );
                }
                // Bullet block (all lines start with -)
                if (lines.every((l) => l.trim().startsWith("- "))) {
                    return (
                        <ul key={i} className="space-y-1.5 list-disc pl-5 marker:text-[var(--brand)]">
                            {lines.map((l, j) => (
                                <li key={j}>{renderInline(l.trim().replace(/^-\s+/, ""))}</li>
                            ))}
                        </ul>
                    );
                }
                return <ProseLines key={i} lines={lines} />;
            })}
        </div>
    );
}

function ProseLines({ lines }) {
    return (
        <p>
            {lines.map((l, i) => (
                <span key={i}>
                    {renderInline(l)}
                    {i < lines.length - 1 && <br />}
                </span>
            ))}
        </p>
    );
}

function renderInline(s) {
    // **bold** segments
    const parts = String(s).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
        /^\*\*[^*]+\*\*$/.test(p) ? (
            <strong key={i} className="text-primary font-medium">{p.slice(2, -2)}</strong>
        ) : (
            <span key={i}>{p}</span>
        )
    );
}


/* Strength variant selector for product families with multiple SKUs.
 * Currently hardcoded for GLP-3R (10 mg + 30 mg). To add more families,
 * append to VARIANT_GROUPS with {slug, strength, price} for each variant.
 */
const VARIANT_GROUPS = [
    {
        match: ["glp-3r-10mg", "glp-3r-30mg"],
        label: "Strength",
        variants: [
            { slug: "glp-3r-10mg", strength: "10 MG" },
            { slug: "glp-3r-30mg", strength: "30 MG" },
        ],
    },
];

function VariantSelector({ product }) {
    const group = VARIANT_GROUPS.find((g) => g.match.includes(product.slug));
    if (!group) return null;
    return (
        <div className="mb-6" data-testid="variant-selector">
            <div className="data-label mb-2">{group.label}</div>
            <div className="flex flex-wrap gap-2">
                {group.variants.map((v) => {
                    const active = v.slug === product.slug;
                    return (
                        <Link
                            key={v.slug}
                            to={`/product/${v.slug}`}
                            data-testid={`variant-${v.strength.replace(/\s/g, "")}`}
                            className={`px-5 py-2 border font-mono text-xs tracking-wider transition-colors ${
                                active
                                    ? "border-[var(--brand)] bg-[var(--brand-soft)] text-brand"
                                    : "border-[var(--border)] text-secondary hover:border-[var(--brand)] hover:text-brand"
                            }`}
                        >
                            {v.strength}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
